"use client"

import { useEffect, useReducer, useState, type ReactNode } from 'react'
import AppSidebar from './AppSidebar'
import Header from './Header'
import { HeaderSlotsProvider } from './PageHeader'
import { getStoredUser, getStoredValue, getRoleName, getCurrentOrganization } from '../../utils/service'

interface MainLayoutProps {
  children: ReactNode
  activeSection?: string
  setActiveSection?: (section: string) => void
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  // Re-read the stored profile when it changes (new picture, name, organization logo).
  const [, refresh] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    window.addEventListener('session-changed', refresh)
    return () => window.removeEventListener('session-changed', refresh)
  }, [])

  const storedUser = getStoredUser()
  const user = {
    name: storedUser ? `${storedUser.first_name} ${storedUser.last_name}`.trim() : '',
    email: storedUser?.email || getStoredValue('email') || '',
    role: getRoleName(),
    initials: storedUser && storedUser.first_name && storedUser.last_name
      ? `${storedUser.first_name[0]}${storedUser.last_name[0]}`.toUpperCase()
      : (storedUser?.email || '?').slice(0, 2).toUpperCase(),
    avatar: storedUser?.avatar_url ?? null,
  }
  const organization = getCurrentOrganization()

  return (
    <HeaderSlotsProvider>
    <div className="min-h-screen w-full bg-primary/20 overflow-hidden">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <AppSidebar user={user} organization={organization} mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />

      <div
        className="fixed top-3 right-3 bottom-3 z-50 flex flex-col bg-white rounded-2xl shadow-sm transition-[left] duration-200"
        style={{ left: 'calc(var(--sidebar-w, 0px) + 0.75rem)' }}
      >
        <Header user={user} onOpenMobileSidebar={() => setMobileSidebarOpen(true)} />
        <main className="flex-1 min-h-0 overflow-y-auto no-scrollbar rounded-b-2xl px-4 pb-4 sm:px-6 sm:pb-6 pt-2">
          {children}
        </main>
      </div>
    </div>
    </HeaderSlotsProvider>
  )
}

export default MainLayout
